import React from 'react';

import { normalizeOptions } from '../Shared/formikFieldHelpers';

import { IntlMessage, useLocale } from '../../Intl';

import type { Field } from '../../../types';
import { isBoolean, isEqual, isInteger, isNil, isNumeric, isString } from '../../../utils';
import formatter from '../../../utils/formatter';
import Table from '../Table';
import type { NumericInputHelperOptions } from '../Text/NumericInputHelper';

type RenderFieldFactoryProps = {
  field: Field;
};

const RenderFieldFactory = ({ field }: RenderFieldFactoryProps) => {
  const { locale } = useLocale();

  if (React.isValidElement(field)) {
    return field;
  }

  // Handle boolean fields
  const isBool = isBoolean(field.value) || ['boolean', 'checkbox'].includes(field.type || '');
  if (isBool) {
    return field?.innerLabel === 'onOff' ? formatter.onOff(field.value) : formatter.yesNo(field.value);
  }

  switch (field.type) {
    case 'month':
      if (isInteger(field.value)) {
        return locale?.localize?.month(parseInt(field.value));
      }
      break;

    case 'number':
      if (isNumeric(field.value)) {
        if (field.options?.style === 'currency') {
          return formatter.currency(field.value, field.options as NumericInputHelperOptions);
        } else if (field.options?.style === 'percent') {
          return formatter.percentage(field.value, field.options as NumericInputHelperOptions);
        }
        return field.value;
      }
      return '-';

    case 'date':
      if (field.value) {
        if (isString(field.value)) {
          return formatter.date(field.value);
        }
        return field.value;
      }
      return '-';

    case 'dayAndMonth':
      if (field.value) {
        const dateParse = new Date(Date.parse(field.value));
        return `${dateParse.getDate()}/${dateParse.getMonth() + 1}`;
      }
      return '-';

    case 'table':
      return <Table field={field} isEditing={false} />;

    case 'select':
    case 'autocomplete':
      return renderSelectField(field);

    case 'text':
      if (field.options?.style === 'select') {
        return renderSelectField(field);
      }

      if (field.value.length === 0) {
        return '-';
      }
      break;
  }

  return <IntlMessage value={field?.value} />;
};

// Helper function to render select fields
const renderSelectField = (field: Field) => {
  const normalizedItems = normalizeOptions({ field });
  const selectedItem = normalizedItems.find(item => isEqual(item.value, field.value));

  if (!isNil(selectedItem)) {
    if (isString(selectedItem.element)) {
      return <IntlMessage value={selectedItem.element} />;
    }
    return <IntlMessage value={selectedItem.value + ''} />;
  }

  return <IntlMessage value={field?.value} />;
};

export default RenderFieldFactory;
